import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
    // setProfilePhoto, // NOTE: setProfilePhoto action might not be needed directly here if thunks handle state update
    uploadProfilePhoto,
    fetchProfilePhoto, // Ensure fetch is imported if needed after upload
    deleteProfilePhoto,
    selectProfilePhoto,
    selectProfileLoading, // Make sure this selector is exported from profileSlice
} from "../../store/slices/profileSlice"; // Use path alias
import Button from "../../components/ui/button/Button"; // Use path alias
import classes from "./ProfilePhotoStep.module.scss";
import { RootState, AppDispatch } from "../../store/store"; // Use path alias
import LoadingOverlay from "../../components/ui/loading/LoadingOverlay"; // Use path alias
import { Avatar, Box, Typography, IconButton } from "@mui/material";
import PersonIcon from "@mui/icons-material/Person";
import AddAPhotoIcon from "@mui/icons-material/AddAPhoto";
import DeleteIcon from "@mui/icons-material/Delete";
import { selectCurrentUser, selectAuthToken } from "../../store/slices/authSlice"; // Changed to named imports

// Assuming CurrentUser type is defined in authSlice or types file
// import { CurrentUser } from '@/types';

const ProfilePhotoStep: React.FC = () => {
    const dispatch = useDispatch<AppDispatch>();
    const currentPhotoUrl = useSelector(selectProfilePhoto);
    const isLoading = useSelector(selectProfileLoading); // Ensure this returns boolean
    const user = useSelector(selectCurrentUser); // Ensure this returns correct User type or null
    const token = useSelector(selectAuthToken); // Get auth token
    const [isProcessing, setIsProcessing] = useState(false);
    const [actionMessage, setActionMessage] = useState("");

    const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        // Ensure user and token exist
        if (file && user?.id && token) {
            const reader = new FileReader();
            reader.onload = async (e) => {
                const photoBase64 = e.target?.result as string;
                setIsProcessing(true);
                setActionMessage("Uploading your photo...");
                try {
                    // // Pass token and photoData correctly
                    // await dispatch(uploadProfilePhoto({ token, photoData: photoBase64 })).unwrap();
                    // // Fetching might be needed if upload thunk doesn't update URL
                    // // await dispatch(fetchProfilePhoto(user.id)).unwrap();
                    dispatch(uploadProfilePhoto({ token, photoData: photoBase64 }))
                        .unwrap()
                        .then(() => {
                            // After upload succeeds, fetch the latest presigned URL using the user's id
                            if (user && user.id) {
                                dispatch(fetchProfilePhoto(user.id));
                            }
                        })
                        .catch((error: any) => {
                            console.error("Upload failed:", error);
                        });
                    // console.log("Photo uploaded successfully");
                } catch (error: any) {
                    console.error("Upload failed:", error);
                } finally {
                    setIsProcessing(false);
                    setActionMessage("");
                }
            };
            reader.readAsDataURL(file);
        }
    };

    const handleRemovePhoto = async () => {
        if (user?.id) {
            setIsProcessing(true);
            setActionMessage("Removing your photo...");
            try {
                await dispatch(deleteProfilePhoto(user.id)).unwrap();
                // console.log("Photo removed successfully");
            } catch (error: any) {
                console.error("Failed to delete profile photo:", error);
            } finally {
                setIsProcessing(false);
                setActionMessage("");
            }
        }
    };

    const getUserFullName = () => {
        if (!user) return "User";
        // Use optional chaining safely
        const firstName = user?.firstName || "";
        const lastName = user?.lastName || "";
        return firstName && lastName ? `${firstName} ${lastName}` : firstName || "User";
    };

    return (
        <Box className={classes.container} textAlign="center">
            {/* Ensure isLoading is boolean */}
            <LoadingOverlay isLoading={!!(isProcessing || isLoading)} message={actionMessage} />

            <Typography variant="h6" gutterBottom>
                Add a Profile Photo (Optional)
            </Typography>
            <Typography variant="body2" color="textSecondary" gutterBottom sx={{ mb: 3 }}>
                {/* Use escaped apostrophes */}
                This helps others recognize you in the community.
            </Typography>

            <Box sx={{ position: "relative", display: "inline-block", mb: 2 }}>
                <Avatar
                    // Ensure currentPhotoUrl is string | undefined
                    src={currentPhotoUrl || undefined}
                    alt="Profile Photo"
                    sx={{ width: 150, height: 150, mb: 2, border: "3px solid grey" }}
                >
                    {!currentPhotoUrl && <PersonIcon sx={{ fontSize: 80 }} />}
                </Avatar>

                <Box sx={{ position: "absolute", bottom: 10, right: 10 }}>
                    {currentPhotoUrl ? (
                        <IconButton
                            color="error"
                            onClick={handleRemovePhoto}
                            // Ensure disabled prop receives boolean
                            disabled={!!(isProcessing || isLoading)}
                            sx={{
                                backgroundColor: "white",
                                "&:hover": { backgroundColor: "#f0f0f0" },
                            }}
                            aria-label="Remove photo"
                        >
                            <DeleteIcon />
                        </IconButton>
                    ) : (
                        <IconButton
                            color="primary"
                            component="label"
                            // Ensure disabled prop receives boolean
                            disabled={!!(isProcessing || isLoading)}
                            sx={{
                                backgroundColor: "white",
                                "&:hover": { backgroundColor: "#f0f0f0" },
                            }}
                            aria-label="Add photo"
                        >
                            <AddAPhotoIcon />
                            <input
                                type="file"
                                hidden
                                accept="image/*"
                                onChange={handleFileChange}
                            />
                        </IconButton>
                    )}
                </Box>
            </Box>

            <Typography variant="h6" className={classes.profileName}>
                {getUserFullName()}
            </Typography>
        </Box>
    );
};

export default ProfilePhotoStep;
